/**
 * The Påminnelser modal of a work order (Detaljer): open state, the
 * schedule draft (admins), validation and the save.
 */
import { useState } from 'react';
import { saveWorkOrderReminderSchedule } from '../../api/workOrderRemindersApi';
import type { WorkOrder } from '../../types/workOrders';
import { ReminderDraft, draftFromTiming, isScheduleOff, scheduleTiming, timingFromDraft } from '../../types/workOrderReminders';

export const useReminderScheduleEditor = (
    workOrder: Pick<WorkOrder, 'key' | 'buildingKey' | 'title' | 'reminders'>,
    onSaved: () => void
) => {
    const [open, setOpen] = useState(false);
    const [draft, setDraft] = useState<ReminderDraft | null>(null);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const schedule = workOrder.reminders?.schedule;
    const start = () => {
        setError(null);
        // Turned off → the box starts unchecked; none yet → checked, empty fields.
        setDraft(draftFromTiming(scheduleTiming(schedule), !isScheduleOff(schedule)));
        setOpen(true);
    };
    /** Read-only: the summary and the sent reminders */
    const view = () => {
        setError(null);
        setDraft(null);
        setOpen(true);
    };
    const cancel = () => {
        setOpen(false);
        setDraft(null);
        setError(null);
    };
    const save = async () => {
        if (!draft) return;
        const result = timingFromDraft(draft);
        if (result.error !== undefined) {
            setError(result.error);
            return;
        }
        setSaving(true);
        setError(null);
        try {
            await saveWorkOrderReminderSchedule(workOrder, result.timing);
            setOpen(false);
            setDraft(null);
            onSaved();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Kunde inte spara påminnelserna');
        } finally {
            setSaving(false);
        }
    };

    return { open, draft, setDraft, saving, error, start, view, cancel, save };
};
